import type { FriendEntry } from "@golazo/shared";
import { lang, t } from "../i18n/index.js";
import type { Profile } from "../game/profile.js";
import { levelFor, ratePlayer } from "../game/rating.js";
import { TROPHIES, type Rarity } from "../game/badges.js";
import "./friendCard.css";
import { rivalryLine, togetherLine } from "./friendText.js";

/**
 * La ficha de un amigo: quien es, donde esta, y como vais el uno contra el otro.
 *
 * La primera version era una tabla de numeros, y si tu amigo no habia jugado
 * online se quedaba en una linea gris sobre un rectangulo vacio ("el card es
 * feisimo", 2026-09-26). Una ficha de amigo tiene que servir para HACER algo
 * —escribirle, meterte en su sala, invitarle a la tuya— y tener cosas que
 * tocar: el cara a cara se despliega fila a fila, los trofeos se filtran, la
 * forma se lee partido a partido.
 *
 * Todo lo comparable sale del perfil ONLINE de cada uno (contra personas).
 */
export interface FriendCardData {
  clientId: string;
  nickname: string;
  /** Su perfil online, o null si aun no ha jugado online. */
  profile: Profile | null;
  /** El tuyo, para comparar y para vuestro historial juntos. */
  mine: Profile;
  /** Su estado en vivo (null si no esta en tu lista, p. ej. eres tu). */
  friend: FriendEntry | null;
  /** Es tu propia ficha (desde la clasificacion). */
  self?: boolean;
}

export interface FriendCardActions {
  message?(clientId: string): void;
  join?(friend: FriendEntry): void;
  invite?(clientId: string): void;
  /** ¿Estas ahora en una sala a la que invitar? */
  canInvite?(): boolean;
  remove?(clientId: string): void;
  block?(clientId: string): void;
}

export interface FriendCardHandle {
  open(data: FriendCardData): void;
  /** Mientras llega su perfil del servidor. */
  loading(clientId: string, nickname: string, friend: FriendEntry | null): void;
  /** Su estado ha cambiado (se conecta, entra en una sala) con la ficha abierta. */
  updateFriend(friend: FriendEntry | null): void;
  /** Para quien esta abierta ahora, o null. */
  openFor(): string | null;
  close(): void;
  dispose(): void;
}

type Tab = "duel" | "together" | "trophies" | "form";
type TrophyFilter = "all" | "theirs" | "mine" | "both";

interface Line {
  label: string;
  mine: number;
  theirs: number;
  fmt: (n: number) => string;
}

const pct = (a: number, b: number): number => (b > 0 ? Math.round((a / b) * 100) : 0);
const per = (a: number, b: number): number => (b > 0 ? a / b : 0);

function escapeHtml(text: string): string {
  return text.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

/** "hace 3 h", "hace 2 días". */
export function agoText(ms: number, now = Date.now()): string {
  const mins = Math.max(1, Math.floor((now - ms) / 60000));
  if (mins < 60) return t("hace {n} min", { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("hace {n} h", { n: hours });
  return t("hace {n} días", { n: Math.floor(hours / 24) });
}

/** Donde esta un amigo ahora mismo, en una frase. */
export function friendWhere(f: FriendEntry): string {
  if (f.status === "playing") return t("Jugando en {sala}", { sala: f.roomName ?? "" });
  if (f.status === "room") return t("En {sala} · {modo}", { sala: f.roomName ?? "", modo: f.roomMode ?? "" });
  if (f.status === "online") return t("Conectado");
  return t("Visto {ago}", { ago: agoText(f.lastSeen) });
}

function sinceText(ms: number): string {
  const date = new Intl.DateTimeFormat(lang() === "en" ? "en-GB" : "es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(ms));
  return t("Amigos desde el {fecha}", { fecha: date });
}

/** Dos letras para el circulo cuando no hay dorsal que enseñar. */
function initials(nickname: string): string {
  const words = nickname.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return [...nickname.trim()].slice(0, 2).join("").toUpperCase() || "?";
}

/** Un color estable por apodo: el mismo amigo siempre del mismo color. */
function hueOf(text: string): number {
  let h = 0;
  for (const c of text) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h % 360;
}

function compareLines(mine: Profile, theirs: Profile): Line[] {
  const m = mine.totals;
  const o = theirs.totals;
  const int = (n: number): string => String(Math.round(n));
  const dec = (n: number): string => n.toFixed(2);
  return [
    { label: t("Media"), mine: mine.overall, theirs: theirs.overall, fmt: int },
    { label: t("Nivel"), mine: levelFor(mine.xp).level, theirs: levelFor(theirs.xp).level, fmt: int },
    { label: t("Partidos"), mine: m.matches, theirs: o.matches, fmt: int },
    { label: t("% victorias"), mine: pct(m.wins, m.matches), theirs: pct(o.wins, o.matches), fmt: (n) => `${n}%` },
    { label: t("Goles por partido"), mine: per(m.goals, m.matches), theirs: per(o.goals, o.matches), fmt: dec },
    { label: t("Asistencias por partido"), mine: per(m.assists, m.matches), theirs: per(o.assists, o.matches), fmt: dec },
    { label: "MVP", mine: m.mvps, theirs: o.mvps, fmt: int },
    { label: t("Mejor racha"), mine: mine.bestWinStreak, theirs: theirs.bestWinStreak, fmt: int },
    { label: t("Récord de goles en un partido"), mine: mine.records.goalsInMatch, theirs: theirs.records.goalsInMatch, fmt: int },
    { label: t("Trofeos"), mine: Object.keys(mine.unlocked).length, theirs: Object.keys(theirs.unlocked).length, fmt: int },
  ];
}

const hasPlayed = (p: Profile | null): p is Profile => Boolean(p && p.totals.matches > 0);

/** La ultima pestaña que se miro: si abres otra ficha, sigues en la misma. */
let lastTab: Tab = "duel";

export function createFriendCard(root: HTMLElement = document.body, actions: FriendCardActions = {}): FriendCardHandle {
  const overlay = document.createElement("div");
  overlay.className = "friend-card is-hidden";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "fc-title");
  overlay.innerHTML = `
    <button type="button" class="friend-card-backdrop" data-fc-close tabindex="-1" aria-label="${t("Cerrar")}"></button>
    <div class="friend-card-sheet">
      <header class="fc-hero" data-fc-hero></header>
      <div class="fc-actions" data-fc-actions></div>
      <div class="fc-menu" data-fc-menu hidden></div>
      <nav class="fc-tabs" data-fc-tabs role="tablist" hidden>
        <button type="button" role="tab" data-fc-tab="duel">${t("Cara a cara")}</button>
        <button type="button" role="tab" data-fc-tab="together">${t("Juntos")}</button>
        <button type="button" role="tab" data-fc-tab="trophies">${t("Trofeos")}</button>
        <button type="button" role="tab" data-fc-tab="form">${t("Forma")}</button>
      </nav>
      <div class="fc-body" data-fc-body></div>
    </div>
  `;
  root.appendChild(overlay);
  const hero = overlay.querySelector("[data-fc-hero]") as HTMLElement;
  const actionBar = overlay.querySelector("[data-fc-actions]") as HTMLElement;
  const menu = overlay.querySelector("[data-fc-menu]") as HTMLElement;
  const tabs = overlay.querySelector("[data-fc-tabs]") as HTMLElement;
  const body = overlay.querySelector("[data-fc-body]") as HTMLElement;
  const sheet = overlay.querySelector(".friend-card-sheet") as HTMLElement;

  let data: FriendCardData | null = null;
  let loadingFor: { clientId: string; nickname: string; friend: FriendEntry | null } | null = null;
  let tab: Tab = lastTab;
  let trophyFilter: TrophyFilter = "all";
  let armed: "remove" | "block" | null = null;
  let armTimer = 0;

  const current = (): { clientId: string; nickname: string; friend: FriendEntry | null; self: boolean } | null =>
    data
      ? { clientId: data.clientId, nickname: data.nickname, friend: data.friend, self: data.self === true }
      : loadingFor
        ? { ...loadingFor, self: false }
        : null;

  const close = (): void => {
    overlay.classList.add("is-hidden");
    data = null;
    loadingFor = null;
    armed = null;
    window.clearTimeout(armTimer);
  };
  for (const el of overlay.querySelectorAll("[data-fc-close]")) el.addEventListener("click", close);
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && !overlay.classList.contains("is-hidden")) {
      event.stopPropagation();
      close();
    }
  };
  window.addEventListener("keydown", onKey, true);

  const button = (label: string, cls: string, run: () => void, title?: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = cls;
    b.textContent = label;
    if (title) {
      b.title = title;
      b.setAttribute("aria-label", title);
    }
    b.addEventListener("click", (ev) => {
      ev.stopPropagation();
      run();
    });
    return b;
  };

  // ------------------------------------------------------------ cabecera

  const renderHero = (): void => {
    const who = current();
    if (!who) return;
    const profile = data?.profile ?? null;
    const f = who.friend;
    const status = who.self ? "self" : (f?.status ?? "offline");
    const number = profile?.identity.shirtNumber;
    const chips: string[] = [];
    if (hasPlayed(profile)) {
      chips.push(`<span class="fc-chip">${t("Nv {n}", { n: levelFor(profile.xp).level })}</span>`);
      chips.push(`<span class="fc-chip is-gold">${t("Media {n}", { n: profile.overall })}</span>`);
      chips.push(`<span class="fc-chip">${t("{n} partidos online", { n: profile.totals.matches })}</span>`);
      const rating = ratePlayer(profile.totals);
      chips.push(`<span class="fc-chip is-style" title="${escapeHtml(rating.archetypeHint)}">${escapeHtml(rating.archetype)}</span>`);
    }
    hero.innerHTML = `
      <div class="fc-avatar is-${status}" style="--fc-hue:${hueOf(who.nickname)}" aria-hidden="true">
        <span>${number ? String(number) : escapeHtml(initials(who.nickname))}</span>
      </div>
      <div class="fc-id">
        <h2 id="fc-title">${escapeHtml(who.nickname)}</h2>
        <p class="fc-status is-${status}">
          <i class="fc-dot"></i>${escapeHtml(who.self ? t("Tu ficha") : f ? friendWhere(f) : t("Desconectado"))}
        </p>
        ${f?.since ? `<p class="fc-since">${escapeHtml(sinceText(f.since))}</p>` : ""}
        ${chips.length > 0 ? `<div class="fc-chips">${chips.join("")}</div>` : ""}
      </div>
      <button type="button" class="fc-close" data-fc-x aria-label="${t("Cerrar")}">×</button>
    `;
    (hero.querySelector("[data-fc-x]") as HTMLElement).addEventListener("click", close);
  };

  // ------------------------------------------------------------ acciones

  const arm = (which: "remove" | "block"): void => {
    armed = which;
    window.clearTimeout(armTimer);
    armTimer = window.setTimeout(() => {
      armed = null;
      renderMenu();
    }, 3000);
    renderMenu();
  };

  const renderMenu = (): void => {
    const who = current();
    menu.replaceChildren();
    if (!who || who.self) return;
    if (actions.remove) {
      menu.appendChild(
        button(armed === "remove" ? t("¿Seguro? Pulsa otra vez") : t("Quitar de amigos"), `fc-btn${armed === "remove" ? " is-armed" : ""}`, () => {
          if (armed !== "remove") return arm("remove");
          actions.remove?.(who.clientId);
          close();
        }),
      );
    }
    if (actions.block) {
      menu.appendChild(
        button(armed === "block" ? t("¿Seguro? Pulsa otra vez") : t("Bloquear"), `fc-btn${armed === "block" ? " is-armed" : ""}`, () => {
          if (armed !== "block") return arm("block");
          actions.block?.(who.clientId);
          close();
        }),
      );
    }
    const hint = document.createElement("p");
    hint.className = "fc-menu-hint";
    hint.textContent = t("Quitarle o bloquearle borra vuestra conversación. Si le bloqueas, no te llegará nada suyo.");
    menu.appendChild(hint);
  };

  /**
   * Los botones de hacer algo con tu amigo. Arriba van todos; en los huecos
   * vacios (`playOnly`) solo los de JUGAR juntos: "Mensaje" ya esta arriba, y
   * repetirlo debajo era ruido.
   */
  const actionButtons = (playOnly = false): HTMLButtonElement[] => {
    const who = current();
    if (!who || who.self) return [];
    const f = who.friend;
    const list: HTMLButtonElement[] = [];
    if (f && f.roomId && f.roomOpen && actions.join) {
      list.push(button(`▶ ${t("Unirse a {sala}", { sala: f.roomName ?? "" })}`, "fc-btn is-primary", () => actions.join?.(f)));
    }
    if (f && f.status !== "offline" && actions.invite) {
      // Con sala, a ella; sin sala, "jugar juntos" monta una y le invita.
      const inRoom = actions.canInvite?.() ?? false;
      list.push(
        button(inRoom ? `＋ ${t("Invitar a tu sala")}` : `⚽ ${t("Jugar juntos")}`, list.length === 0 ? "fc-btn is-primary" : "fc-btn", () => {
          actions.invite?.(who.clientId);
        }),
      );
    }
    if (playOnly) return list;
    if (actions.message && f) {
      list.push(button(`✉ ${t("Mensaje")}`, list.length === 0 ? "fc-btn is-primary" : "fc-btn", () => actions.message?.(who.clientId)));
    }
    if ((actions.remove || actions.block) && f) {
      list.push(
        button("⋯", "fc-btn fc-btn--icon", () => {
          menu.hidden = !menu.hidden;
          if (!menu.hidden) renderMenu();
        }, t("Más opciones")),
      );
    }
    return list;
  };

  const renderActions = (): void => {
    const buttons = actionButtons();
    actionBar.replaceChildren(...buttons);
    actionBar.hidden = buttons.length === 0;
  };

  // ------------------------------------------------------------ pestañas

  const setTab = (next: Tab): void => {
    tab = next;
    lastTab = next;
    renderBody();
  };
  for (const b of tabs.querySelectorAll<HTMLElement>("[data-fc-tab]")) {
    b.addEventListener("click", () => setTab(b.dataset.fcTab as Tab));
  }

  const syncTabs = (): void => {
    for (const b of tabs.querySelectorAll<HTMLElement>("[data-fc-tab]")) {
      const on = b.dataset.fcTab === tab;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    }
  };

  /**
   * Lo que llevais de verdad: partidos online que el servidor rehizo el mismo.
   * Va encima de la comparacion de numeros porque es lo que se discute.
   */
  const h2hHtml = (d: FriendCardData): string => {
    const f = d.friend;
    const vs = rivalryLine(d.nickname, f?.vs);
    const together = togetherLine(f?.with);
    if (!vs && !together) return "";
    const record = f?.vs ? `${f.vs[0]}-${f.vs[1]}-${f.vs[2]}` : "";
    return `
      <div class="fc-h2h">
        ${record ? `<strong title="${escapeHtml(t("victorias-empates-derrotas"))}">⚔ ${record}</strong>` : ""}
        <span>${escapeHtml(vs ?? "")}${vs && together ? " · " : ""}${escapeHtml(together ?? "")}</span>
      </div>`;
  };

  const renderDuel = (d: FriendCardData & { profile: Profile }): void => {
    const lines = compareLines(d.mine, d.profile);
    const mineLeads = lines.filter((l) => l.mine > l.theirs).length;
    const theirLeads = lines.filter((l) => l.theirs > l.mine).length;
    const verdict =
      d.mine.totals.matches === 0
        ? t("Aún no has jugado online: de momento, gana por incomparecencia.")
        : mineLeads > theirLeads
          ? t("Vas por delante en {a} de {n}. Que no se te suba.", { a: mineLeads, n: lines.length })
          : theirLeads > mineLeads
            ? t("{nick} te gana en {b} de {n}. Hay trabajo.", { nick: d.nickname, b: theirLeads, n: lines.length })
            : t("Empate técnico: {a} para cada uno.", { a: mineLeads });
    body.innerHTML = `
      <div class="fc-duel">
        <figure class="fc-radar" data-fc-radar></figure>
        <div class="fc-duel-main">
          ${h2hHtml(d)}
          <p class="fc-verdict">${escapeHtml(verdict)}</p>
          <div class="fc-vs-head"><span>${t("Tú")}</span><span>${escapeHtml(d.nickname)}</span></div>
          <ul class="fc-rows" data-fc-rows></ul>
          <p class="fc-tip">${t("Toca una fila para ver la diferencia.")}</p>
        </div>
      </div>
    `;
    const rows = body.querySelector("[data-fc-rows]") as HTMLElement;
    for (const l of lines) {
      const lead = l.mine === l.theirs ? "tie" : l.mine > l.theirs ? "mine" : "theirs";
      const total = l.mine + l.theirs;
      const share = total > 0 ? Math.round((l.mine / total) * 100) : 50;
      const li = document.createElement("li");
      li.className = `fc-row is-${lead}`;
      li.tabIndex = 0;
      li.setAttribute("role", "button");
      li.setAttribute("aria-expanded", "false");
      const label = l.label === "MVP" ? "MVP" : l.label.charAt(0).toLowerCase() + l.label.slice(1);
      const diff = l.fmt(Math.abs(l.mine - l.theirs));
      const detail =
        lead === "mine"
          ? t("Le sacas {d} en {what}.", { d: diff, what: label })
          : lead === "theirs"
            ? t("Te saca {d} en {what}.", { d: diff, what: label })
            : t("Igualados en {what}.", { what: label });
      li.innerHTML = `
        <div class="fc-row-top">
          <b class="fc-v is-mine">${escapeHtml(l.fmt(l.mine))}</b>
          <span class="fc-row-label">${escapeHtml(l.label)}</span>
          <b class="fc-v is-theirs">${escapeHtml(l.fmt(l.theirs))}</b>
        </div>
        <div class="fc-bar" aria-hidden="true"><i class="fc-bar-mine" style="width:${share}%"></i><i class="fc-bar-theirs" style="width:${100 - share}%"></i></div>
        <p class="fc-row-detail" hidden>${escapeHtml(detail)}</p>
      `;
      const toggle = (): void => {
        const detailEl = li.querySelector(".fc-row-detail") as HTMLElement;
        detailEl.hidden = !detailEl.hidden;
        li.setAttribute("aria-expanded", detailEl.hidden ? "false" : "true");
        li.classList.toggle("is-open", !detailEl.hidden);
      };
      li.addEventListener("click", toggle);
      li.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          toggle();
        }
      });
      rows.appendChild(li);
    }
    renderRadar(body.querySelector("[data-fc-radar]") as HTMLElement, d);
  };

  /**
   * El radar de los seis ejes, los dos a la vez. Es lo que dice de un vistazo
   * en que sois distintos: uno remata y el otro roba. Tocar un eje dice los
   * dos numeros.
   */
  const renderRadar = (host: HTMLElement, d: FriendCardData & { profile: Profile }): void => {
    const mine = ratePlayer(d.mine.totals).attributes;
    const theirs = ratePlayer(d.profile.totals).attributes;
    const size = 240;
    const c = size / 2;
    const r = 78;
    const n = mine.length;
    const point = (i: number, value: number, scale = 1): [number, number] => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      // La escala empieza en el suelo de la ficha (40): nadie baja de ahi.
      const k = scale * Math.max(0.06, Math.min(1, (value - 38) / 61));
      return [c + Math.cos(angle) * r * k, c + Math.sin(angle) * r * k];
    };
    const poly = (values: number[]): string => values.map((v, i) => point(i, v).join(",")).join(" ");
    const rings = [0.33, 0.66, 1]
      .map((k) => `<polygon class="fc-radar-ring" points="${mine.map((_, i) => point(i, 99, k).join(",")).join(" ")}" />`)
      .join("");
    const axes = mine
      .map((a, i) => {
        const [x, y] = point(i, 99, 1);
        const [lx, ly] = point(i, 99, 1.28);
        return `<line class="fc-radar-axis" x1="${c}" y1="${c}" x2="${x}" y2="${y}" />
          <text class="fc-radar-label" x="${lx}" y="${ly}" data-axis="${i}" tabindex="0" role="button">${escapeHtml(a.label)}</text>`;
      })
      .join("");
    host.innerHTML = `
      <svg viewBox="0 0 ${size} ${size}" role="img" aria-label="${t("Tus atributos y los suyos")}">
        ${rings}${axes}
        <polygon class="fc-radar-theirs" points="${poly(theirs.map((a) => a.value))}" />
        <polygon class="fc-radar-mine" points="${poly(mine.map((a) => a.value))}" />
      </svg>
      <figcaption data-fc-radar-caption>
        <span class="fc-legend is-mine">${t("Tú")}</span>
        <span class="fc-legend is-theirs">${escapeHtml(d.nickname)}</span>
      </figcaption>
    `;
    const caption = host.querySelector("[data-fc-radar-caption]") as HTMLElement;
    const legend = caption.innerHTML;
    let shown: number | null = null;
    const showAxis = (i: number): void => {
      for (const el of host.querySelectorAll<SVGTextElement>("[data-axis]")) el.classList.toggle("is-on", Number(el.dataset.axis) === i && shown !== i);
      if (shown === i) {
        shown = null;
        caption.innerHTML = legend;
        return;
      }
      shown = i;
      caption.innerHTML = `<strong>${escapeHtml(mine[i]!.label)}</strong> · ${escapeHtml(mine[i]!.hint)}<br>
        <span class="fc-legend is-mine">${t("Tú")} ${mine[i]!.value}</span>
        <span class="fc-legend is-theirs">${escapeHtml(d.nickname)} ${theirs[i]!.value}</span>`;
    };
    for (const el of host.querySelectorAll<SVGTextElement>("[data-axis]")) {
      const i = Number(el.dataset.axis);
      el.addEventListener("click", () => showAxis(i));
      el.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          showAxis(i);
        }
      });
    }
  };

  const renderTogether = (d: FriendCardData): void => {
    const together = d.mine.partners.find((p) => p.nickname === d.nickname);
    if (!together) {
      body.innerHTML = `
        <div class="fc-empty fc-empty--small">
          <div class="fc-empty-art" aria-hidden="true">🤝</div>
          <h3>${t("Aún no habéis jugado en el mismo equipo")}</h3>
          <p>${t("La primera vez siempre cuesta. Métete en su sala o tráetelo a la tuya.")}</p>
          <div class="fc-empty-cta" data-fc-cta></div>
        </div>
      `;
      (body.querySelector("[data-fc-cta]") as HTMLElement).append(...actionButtons(true));
      return;
    }
    const winPct = pct(together.won, together.played);
    const verdict =
      winPct >= 60
        ? t("Juntos sois un problema para cualquiera.")
        : winPct >= 40
          ? t("Os entendéis. Casi siempre.")
          : t("Juntos perdéis más de lo que ganáis. Seguro que es culpa suya.");
    body.innerHTML = `
      <div class="fc-together">
        <div class="fc-ring" style="--fc-pct:${winPct}" aria-label="${t("{pct}% de victorias juntos", { pct: winPct })}">
          <strong>${winPct}%</strong><span>${t("ganados")}</span>
        </div>
        <div class="fc-together-stats">
          <div><strong>${together.played}</strong><span>${t("partidos juntos")}</span></div>
          <div><strong>${together.won}</strong><span>${t("victorias")}</span></div>
          <div><strong>${escapeHtml(agoText(together.lastAt))}</strong><span>${t("la última vez")}</span></div>
        </div>
        <p class="fc-verdict">${escapeHtml(verdict)}</p>
        <div class="fc-empty-cta" data-fc-cta></div>
      </div>
    `;
    (body.querySelector("[data-fc-cta]") as HTMLElement).append(...actionButtons(true));
  };

  const renderTrophies = (d: FriendCardData & { profile: Profile }): void => {
    const theirs = d.profile.unlocked;
    const mine = d.mine.unlocked;
    const stateOf = (id: string): "both" | "theirs" | "mine" | "none" =>
      id in theirs && id in mine ? "both" : id in theirs ? "theirs" : id in mine ? "mine" : "none";
    const shown = TROPHIES.filter((tr) => {
      const st = stateOf(tr.id);
      return trophyFilter === "all" || st === trophyFilter;
    });
    const count = (st: string): number => TROPHIES.filter((tr) => stateOf(tr.id) === st).length;
    const filters: Array<[TrophyFilter, string]> = [
      ["all", t("Todos")],
      ["theirs", t("Solo suyos · {n}", { n: count("theirs") })],
      ["mine", t("Solo tuyos · {n}", { n: count("mine") })],
      ["both", t("De los dos · {n}", { n: count("both") })],
    ];
    const rarityClass = (r: Rarity): string => `is-${r}`;
    body.innerHTML = `
      <p class="fc-verdict">${t("{nick} tiene {a} de {n} trofeos. Tú, {b}.", {
        nick: escapeHtml(d.nickname),
        a: Object.keys(theirs).length,
        n: TROPHIES.length,
        b: Object.keys(mine).length,
      })}</p>
      <div class="fc-filters" role="group" aria-label="${t("Filtrar trofeos")}" data-fc-filters></div>
      <ul class="fc-trophies">
        ${
          shown.length === 0
            ? `<li class="fc-trophies-empty">${t("Nada por aquí.")}</li>`
            : shown
                .map((tr) => {
                  const st = stateOf(tr.id);
                  const owner =
                    st === "both" ? t("Los dos") : st === "theirs" ? escapeHtml(d.nickname) : st === "mine" ? t("Tú") : t("Nadie aún");
                  return `<li class="fc-trophy is-${st} ${rarityClass(tr.rarity)}" title="${escapeHtml(tr.hint)}">
                    <span class="fc-trophy-icon" aria-hidden="true">${tr.icon}</span>
                    <span class="fc-trophy-name">${escapeHtml(tr.label)}</span>
                    <small class="fc-trophy-owner">${owner}</small>
                  </li>`;
                })
                .join("")
        }
      </ul>
    `;
    const bar = body.querySelector("[data-fc-filters]") as HTMLElement;
    for (const [id, label] of filters) {
      bar.appendChild(
        button(label, `fc-filter${trophyFilter === id ? " is-active" : ""}`, () => {
          trophyFilter = id;
          renderBody();
        }),
      );
    }
  };

  const renderForm = (d: FriendCardData & { profile: Profile }): void => {
    const recent = d.profile.recent.slice(0, 10);
    const streak =
      d.profile.winStreak >= 2
        ? t("En racha: {n} victorias seguidas.", { n: d.profile.winStreak })
        : d.profile.dayStreak >= 2
          ? t("Lleva {n} días seguidos jugando.", { n: d.profile.dayStreak })
          : t("Su mejor racha: {n} victorias seguidas.", { n: d.profile.bestWinStreak });
    body.innerHTML = `
      <p class="fc-verdict">${escapeHtml(streak)}</p>
      <ol class="fc-matches">
        ${recent
          .map((m) => {
            const chip = m.result === "win" ? "✓" : m.result === "draw" ? "=" : "✗";
            const bits = [
              m.goals > 0 ? `⚽ ${m.goals}` : "",
              m.assists > 0 ? `🅰 ${m.assists}` : "",
              m.mvp ? "★ MVP" : "",
            ]
              .filter(Boolean)
              .join(" · ");
            return `<li class="fc-match is-${m.result}">
              <span class="fc-form-chip is-${m.result}" aria-hidden="true">${chip}</span>
              <strong class="fc-match-score">${m.goalsFor}-${m.goalsAgainst}</strong>
              <span class="fc-match-mode">${escapeHtml(m.modeId)}</span>
              <span class="fc-match-bits">${escapeHtml(bits)}</span>
              <small class="fc-match-ago">${escapeHtml(agoText(m.at))}</small>
            </li>`;
          })
          .join("")}
      </ol>
    `;
  };

  /** Aun no ha jugado online: se enseña lo que vendra, y como provocarlo. */
  const renderEmpty = (d: FriendCardData): void => {
    const mine = d.mine;
    const ghost: Array<[string, string]> = [
      [t("Media"), hasPlayed(mine) ? String(mine.overall) : "—"],
      [t("Goles por partido"), hasPlayed(mine) ? per(mine.totals.goals, mine.totals.matches).toFixed(2) : "—"],
      ["MVP", hasPlayed(mine) ? String(mine.totals.mvps) : "—"],
      [t("Trofeos"), String(Object.keys(mine.unlocked).length)],
    ];
    body.innerHTML = `
      <div class="fc-empty">
        <div class="fc-empty-art" aria-hidden="true">⚽</div>
        <h3>${t("{nick} aún no ha estrenado el online", { nick: escapeHtml(d.nickname) })}</h3>
        <p>${t("Cuando juegue, aquí veréis quién manda: media, goles, MVP y trofeos, cara a cara.")}</p>
        <div class="fc-empty-cta" data-fc-cta></div>
        <div class="fc-vs-head"><span>${t("Tú")}</span><span>${escapeHtml(d.nickname)}</span></div>
        <ul class="fc-rows is-ghost">
          ${ghost
            .map(
              ([label, value]) => `<li class="fc-row">
                <div class="fc-row-top"><b class="fc-v is-mine">${escapeHtml(value)}</b><span class="fc-row-label">${escapeHtml(label)}</span><b class="fc-v is-theirs">?</b></div>
                <div class="fc-bar" aria-hidden="true"><i class="fc-bar-mine" style="width:50%"></i><i class="fc-bar-theirs" style="width:50%"></i></div>
              </li>`,
            )
            .join("")}
        </ul>
      </div>
    `;
    (body.querySelector("[data-fc-cta]") as HTMLElement).append(...actionButtons(true));
  };

  const renderBody = (): void => {
    sheet.classList.remove("has-tabs");
    if (!data) {
      tabs.hidden = true;
      body.innerHTML = `
        <div class="fc-skeleton" aria-busy="true">
          <p>${t("Cargando su ficha…")}</p>
          <i></i><i></i><i></i><i></i>
        </div>
      `;
      return;
    }
    const d = data;
    if (!hasPlayed(d.profile)) {
      tabs.hidden = true;
      renderEmpty(d);
      return;
    }
    const withProfile = d as FriendCardData & { profile: Profile };
    tabs.hidden = false;
    // Con pestañas, alto fijo: si la ventana cambia de tamaño a cada pestaña,
    // los botones de las pestañas se mueven bajo el dedo.
    sheet.classList.add("has-tabs");
    syncTabs();
    if (tab === "duel") renderDuel(withProfile);
    else if (tab === "together") renderTogether(d);
    else if (tab === "trophies") renderTrophies(withProfile);
    else renderForm(withProfile);
  };

  const renderAll = (): void => {
    renderHero();
    renderActions();
    menu.hidden = true;
    renderBody();
  };

  return {
    loading(clientId, nickname, friend) {
      data = null;
      loadingFor = { clientId, nickname, friend };
      overlay.classList.remove("is-hidden");
      renderAll();
    },

    open(next) {
      data = next;
      loadingFor = null;
      trophyFilter = "all";
      overlay.classList.remove("is-hidden");
      renderAll();
    },

    updateFriend(friend) {
      if (overlay.classList.contains("is-hidden")) return;
      if (data) data = { ...data, friend };
      else if (loadingFor) loadingFor = { ...loadingFor, friend };
      renderHero();
      renderActions();
    },

    openFor() {
      return overlay.classList.contains("is-hidden") ? null : (current()?.clientId ?? null);
    },

    close,
    dispose() {
      close();
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
    },
  };
}
